import React, { useReducer, useState, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'validate.js';

import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import { truncateText } from 'dpl/shared/utils';
import BreederProfileTestimonialModal, {
  useTestimonialModal
} from 'dpl/containers/BreederProfile/Index/BreederProfileTestimonialModal';
import { isNumeric } from 'dpl/shared/form/utils';
import { pushParams } from 'dpl/actions/query_params';
import {
  BREEDER_PROFILE_ID,
  TESTIMONIAL_MODAL_PAGE
} from 'dpl/constants/query_params';

import { OwnerStoryGallery } from './OwnerStoryGallery';

const COLLAPSED_DESCRIPTION_LENGTH = 200;

export default function OwnerStory({
  className,
  date,
  description,
  gallery,
  user,
  breederProfile,
  id,
  expandInModal,
  isExpanded
}) {
  const [selectedStory, setSelectedStory] = useState(null);
  const { open: openTestimonialModal } = useTestimonialModal();
  const params = useSelector(server => server.queryParams);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!isNumeric(params[TESTIMONIAL_MODAL_PAGE]) && !isEmpty(selectedStory)) {
      setSelectedStory(null);
    }
  }, [params]);

  useEffect(() => {
    if (selectedStory && breederProfile) {
      dispatch(
        pushParams({ [BREEDER_PROFILE_ID]: breederProfile.id }, { hash: true })
      );
    }
  }, [selectedStory]);

  const truncatedDescription = truncateText(
    description,
    COLLAPSED_DESCRIPTION_LENGTH
  );

  const isCollapsible = !isExpanded && description !== truncatedDescription;

  const [isCollapsed, toggleIsCollapsed] = useReducer(
    collapsed => !collapsed,
    isCollapsible
  );

  function onExpansion() {
    if (expandInModal) {
      setSelectedStory(id);
      openTestimonialModal(id);
    } else {
      toggleIsCollapsed();
    }
  }

  return (
    <div className={classnames('OwnerStory', className)}>
      <div className="flex items-center gap-12 md:gap-16 mb6">
        <UserAvatar user={user} size="default" />
        <div className="lh-copy">
          <p className="font-16 fw-medium">{user.name}</p>
          <p className="font-14 secondary">Verified owner · {date}</p>
        </div>
      </div>
      {gallery.length > 0 && <OwnerStoryGallery gallery={gallery} />}
      <p
        className={classnames('font-14 md:font-16', {
          'pre-wrap': !isCollapsed
        })}
      >
        {isCollapsed ? truncatedDescription : description}
      </p>
      {isCollapsible && (
        <button
          type="button"
          className="font-14 md:font-16 stone-700 underline mt2"
          onClick={onExpansion}
        >
          {isCollapsed ? 'Show more' : 'Show less'}
        </button>
      )}
      {breederProfile && (
        <div className="flex gap-4 bt b--default pt4 mt4">
          <UserAvatar user={breederProfile} size="sm" />
          <div>
            <p className="font-16">Raised by {breederProfile.name}</p>
            <p className="font-12 secondary">
              of{' '}
              <a
                className="underline"
                href={breederProfile.profile_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {breederProfile.program_name}
              </a>
            </p>
          </div>
        </div>
      )}
      {selectedStory && (
        <BreederProfileTestimonialModal testimonialId={selectedStory.id} />
      )}
    </div>
  );
}

OwnerStory.propTypes = {
  className: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  date: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  gallery: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      type: PropTypes.string,
      caption: PropTypes.string,
      url: PropTypes.string.isRequired,
      url_small: PropTypes.string.isRequired,
      three_two_thumbnail_url: PropTypes.string.isRequired
    })
  ).isRequired,
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    initials: PropTypes.string.isRequired,
    profile_photo_url: PropTypes.string
  }).isRequired,
  breederProfile: PropTypes.shape({
    id: PropTypes.number.isRequired,
    program_name: PropTypes.string.isRequired,
    initials: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    profile_photo_url: PropTypes.string,
    profile_url: PropTypes.string.isRequired
  }),
  expandInModal: PropTypes.bool,
  isExpanded: PropTypes.bool
};

OwnerStory.defaultProps = {
  className: null,
  breederProfile: null,
  expandInModal: false,
  isExpanded: false
};
